import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckboxList,
  DialogOverlayActions,
  Ellipsis,
  ListItem,
  Skeleton,
  TransitioningDialogOverlay,
} from '@fs/zion-ui'
import { Route, useHistory } from '@fs/zion-router'
import { ContentAdd } from '@fs/zion-icon'
import playlistsConfig from '../../../../config/feature/playlist'
import PillButton from '../../../../lib/buttons/PillButton'
import UpdatePlaylistForm from '../UpdatePlaylistForm'
import { usePlaylists } from '../../../../hooks/playlist/usePlaylists'
import { usePlaylistCreation } from '../../../../hooks/playlist/usePlaylistCreation'

const Loading = (): JSX.Element => (
  <>
    <Skeleton.Checkbox />
    <Skeleton.Checkbox />
    <Skeleton.Checkbox />
  </>
)

const UpdatePlaylist = ({ sessionId, onPlaylistUpdated }): JSX.Element => {
  const [t] = useTranslation()
  const { playlists } = usePlaylists()

  const onChange = (selectedIds, lastTouched: { name: string; checked: boolean }): void => {
    const { name: playlistId } = lastTouched
    console.log({ selectedIds, lastTouched })
    console.log(`adding ${sessionId} to playlist ${playlistId}`)
    // TODO: updatePlaylist(playlistId)
    onPlaylistUpdated(selectedIds)
  }
  return (
    <CheckboxList onSelectionChange={onChange}>
      {playlists
        .filter((p) => !p.parent)
        .map((playlist) => {
          const full = !playlist.default && playlist.size >= playlistsConfig.sessionLimit

          let secondaryText = t('playlists.playlist.size', '{ playlistSize } videos', {
            playlistSize: playlist.size || 0,
          })
          if (full) {
            secondaryText = t('playlists.playlists.size-status', '{ playlistSize } videos (FULL)', {
              playlistSize: playlist.size,
            })
          }

          return (
            <ListItem
              data-testid={`${playlist.default ? 'watch-later' : playlist.name}-playlist`}
              dense
              disabled={full}
              primaryText={
                <Ellipsis>
                  <strong>{playlist.name}</strong>
                </Ellipsis>
              }
              secondaryText={secondaryText}
              key={playlist.id}
              name={playlist.id}
              linkName={`Add to ${playlist.default ? 'Default' : 'Other'} Playlist`}
            />
          )
        })}
    </CheckboxList>
  )
}

const CreatePlaylist = ({ sessionId }): JSX.Element => {
  const history = useHistory()
  const { createPlaylist } = usePlaylistCreation()

  const onSubmit = async ({ name, description }): Promise<void> => {
    await createPlaylist({ name, description, initialItem: sessionId || undefined })
    history.goBack()
  }

  return <UpdatePlaylistForm onSubmit={onSubmit} />
}

export const PlaylistManagementOverlay = ({ sessionId, overlay, onPlaylistUpdated }): JSX.Element => {
  const [t] = useTranslation()
  const { playlists, isLoading } = usePlaylists()

  const canCreatePlaylists = playlists.filter((p) => !p.parent).length < playlistsConfig.playlistLimit

  return (
    <TransitioningDialogOverlay
      {...overlay}
      size="sm"
      title={t('playlists.overlay.heading', 'Add to...')}
      initialLocation="/update"
    >
      <Route
        path="/update"
        footer={
          <DialogOverlayActions>
            <PillButton
              Icon={ContentAdd}
              color="blue"
              disabled={!canCreatePlaylists}
              emphasis="low"
              linkName="Create Playlist"
              data-testid="create-playlist"
              to="/create"
            >
              {t('playlists.collection.create.action', 'Create Playlist')}
            </PillButton>
          </DialogOverlayActions>
        }
      >
        {isLoading ? <Loading /> : <UpdatePlaylist sessionId={sessionId} onPlaylistUpdated={onPlaylistUpdated} />}
      </Route>
      <Route path="/create" title={t('playlists.create.title', 'Create New Playlist')}>
        <CreatePlaylist sessionId={sessionId} />
      </Route>
    </TransitioningDialogOverlay>
  )
}
